import { useTranslation } from "react-i18next";
import { Briefcase, Calendar, Call, Sms } from "iconsax-react";
import { Badge } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import { type GetApplicationsResponse } from "../../../types";
import { STATUS_MAP } from "../../../consts";
import { getApplicationDisplayStatus } from "../../../utils";
import { type UseHiringActionsReturn, type UseHiringMutationsReturn } from "../../../hooks";
import ApplicationActionsButtons from "./ApplicationActionsButtons";

interface Props {
  applications: GetApplicationsResponse[];
  onOpenApplication: (id: number) => void;
  actions: UseHiringActionsReturn;
  mutations: UseHiringMutationsReturn;
}

export default function HiringCardsView({
  applications,
  onOpenApplication,
  actions,
  mutations,
}: Props) {
  const { t } = useTranslation("HiringPage");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {applications.map((app) => {
          return (
            <div
              key={app.id}
              role="button"
              tabIndex={0}
              className="flex flex-col gap-4 surface-component-fill radius-lg p-5 cursor-pointer hover:shadow-md"
              onClick={() => onOpenApplication(app.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenApplication(app.id); } }}>
              <div className="flex flex-col gap-2">
                <Badge
                  variant="soft"
                  color={STATUS_MAP[getApplicationDisplayStatus(app)].color}
                  text={t(STATUS_MAP[getApplicationDisplayStatus(app)].label)}
                  icon={STATUS_MAP[getApplicationDisplayStatus(app)].icon}
                />
                <p className="text-body-bold-lg content-base-primary">{app.candidate_name}</p>
              </div>

              <div className="flex flex-col">
                <div className="flex flex-col border-b surface-base-stroke py-3 gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Sms size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{app.invitation_email || "—"}</p>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Call size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{app.phone || "—"}</p>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Briefcase size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">{app.job_position || "—"}</p>
                  </div>
                </div>

                <div className="flex flex-col pt-3 gap-1.5">
                  <p className="content-base-secondary text-body-bold-xs">{t("cards.dateInvited")}</p>

                  <div className="flex items-center gap-1.5">
                    <span className="content-action-brand">
                      <Calendar size={18} color="currentColor" />
                    </span>
                    <p className="text-body-regular-sm content-base-primary">
                      {formatDateForDisplay(app.invitation_date, false) || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <ApplicationActionsButtons
                application={app}
                isLoading={mutations.isLoading}
                reviewingApplicationId={mutations.reviewingApplicationId}
                rejectingApplicationId={mutations.rejectingApplicationId}
                onAction={actions.handleAction}
              />
            </div>
          );
        })}
      </div>
  );
}

